/**
 * A round button at the bottom corners, its name under it: Build at the right, Values at the left.
 * Each has a colour of its own (`color`: Build's orange, a machine's own); Values shows its gauges
 * live: a ring round the button and bars in it.
 */
import type { CSSProperties, JSX } from 'react';

import { Icon } from '@ui/icons';

import type { Gauge } from './actionLook';

import './side.css';

/** The ring's radius in the 58-unit button. */
const RING = 25;
const RING_LENGTH = 2 * Math.PI * RING;
/** The bars: how wide, how far apart, how tall at most, where they stand. */
const BAR = 5;
const GAP = 4;
const TALL = 18;
const FLOOR = 38;

function GaugeArt({ gauge }: { gauge: Gauge }): JSX.Element {
  const left = 29 - (gauge.bars.length * BAR + (gauge.bars.length - 1) * GAP) / 2;
  return (
    <svg className="gauge" viewBox="0 0 58 58" aria-hidden="true">
      <circle className="track" cx="29" cy="29" r={RING} />
      <circle
        className="fill"
        cx="29"
        cy="29"
        r={RING}
        strokeDasharray={`${(RING_LENGTH * gauge.ring).toFixed(1)} ${RING_LENGTH.toFixed(1)}`}
        transform="rotate(-90 29 29)"
      />
      {gauge.bars.map((value, k) => {
        const h = Math.max(2, value * TALL);
        const x = left + k * (BAR + GAP);
        return (
          <g key={k}>
            <rect className="bar-track" x={x} y={FLOOR - TALL} width={BAR} height={TALL} rx={2} />
            <rect className="bar" x={x} y={FLOOR - h} width={BAR} height={h} rx={2} />
          </g>
        );
      })}
    </svg>
  );
}

export function SideButton({
  id,
  icon,
  name,
  aria,
  pressed,
  disabled = false,
  onPress,
  data,
  color,
  gauge,
}: {
  id?: string;
  icon: string;
  name: string;
  aria: string;
  /** For a button that opens something: whether it is open. */
  pressed?: boolean;
  disabled?: boolean;
  onPress: () => void;
  /** `data-*` attributes, for styles and tests. */
  data?: Record<`data-${string}`, string>;
  /** Its own colour: a glossy disc of it. Without, plain glass. */
  color?: string | undefined;
  /** Live values, drawn instead of the icon. */
  gauge?: Gauge | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      id={id}
      className={`side-btn${color ? ' toned' : ''}`}
      style={color ? ({ '--tone': color } as CSSProperties) : undefined}
      aria-label={aria}
      aria-expanded={pressed}
      aria-disabled={disabled}
      onClick={onPress}
      {...data}
    >
      <span className={`disc${color ? '' : ' glass'}`}>
        {gauge ? <GaugeArt gauge={gauge} /> : <Icon id={icon} />}
      </span>
      <span className="name glass">{name}</span>
    </button>
  );
}
